/**
 * Audio-Waveform-Decoder + In-Memory-Cache.
 *
 * Verantwortlich dafür, eine Quell-URL eines Audio-Files in eine kompakte
 * Peak-Repräsentation (Float32Array, jeweils ein Peak pro Sample-Bucket)
 * umzuwandeln. Wir benutzen WebAudio (`AudioContext.decodeAudioData`) — das
 * funktioniert in allen modernen Browsern, kostet beim ersten Aufruf einmalig
 * Bandbreite + Decode-Zeit (typ. 50–300ms) und liefert anschliessend
 * verlustfreie Spitzenwerte für ein Canvas-Rendering.
 *
 * Das Modul ist absichtlich simpel:
 *  - Pro URL gibts genau eine Decode-Promise (deduped). Mehrere Bars die
 *    dieselbe Datei zeigen warten alle auf das gleiche Ergebnis.
 *  - Der Cache wird nicht persistiert — beim Reload neu decodiert. Reicht
 *    fürs Edit-UX, weil die Decode-Zeit auf typischen Voiceover-Dateien
 *    (1–10MB MP3) unter 200ms liegt.
 *  - Die Peak-Auflösung ist NICHT zoom-abhängig: wir cachen 4000 Buckets,
 *    der Renderer downsamplet beim Zeichnen weiter. So decodieren wir nicht
 *    bei jedem Zoom neu.
 */

const PEAK_BUCKETS = 4000;

export type WaveformPeaks = {
  /** Peak-Wert (0..1) pro Bucket. Länge = `PEAK_BUCKETS`. */
  peaks: Float32Array;
  /** Ursprüngliche Datei-Dauer in Sekunden — wichtig für Trim-/Schnittpunkt-Visualisierung. */
  durationSec: number;
};

const cache = new Map<string, WaveformPeaks>();
const inflight = new Map<string, Promise<WaveformPeaks>>();

let sharedAc: AudioContext | null = null;
function getAc(): AudioContext {
  if (sharedAc) return sharedAc;
  // Cast für TS — Webkit-Vendor-Prefix ist seit Jahren weg, aber TS-Lib
  // setzt das Konstrukt nicht voraus.
  const Ctor = (window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext) as typeof AudioContext;
  sharedAc = new Ctor();
  return sharedAc;
}

/**
 * Liefert eine Peak-Repräsentation für die übergebene Audio-URL. Mehrere
 * parallele Aufrufe für dieselbe URL teilen die Promise.
 */
export async function getWaveformPeaks(src: string): Promise<WaveformPeaks> {
  const cached = cache.get(src);
  if (cached) return cached;
  const pending = inflight.get(src);
  if (pending) return pending;

  const promise = decodeAndComputePeaks(src)
    .then((result) => {
      cache.set(src, result);
      inflight.delete(src);
      return result;
    })
    .catch((err) => {
      inflight.delete(src);
      throw err;
    });
  inflight.set(src, promise);
  return promise;
}

async function decodeAndComputePeaks(src: string): Promise<WaveformPeaks> {
  const res = await fetch(src, { credentials: "include" });
  if (!res.ok) throw new Error(`Waveform-Fetch fehlgeschlagen (${res.status})`);
  const arrayBuf = await res.arrayBuffer();
  const ac = getAc();
  // `decodeAudioData` mutiert das Buffer; wir geben es per-call frisch rein.
  const audioBuf: AudioBuffer = await new Promise((resolve, reject) => {
    ac.decodeAudioData(arrayBuf.slice(0), resolve, reject);
  });

  // Peak-Reduktion: pro Bucket den max-abs-Wert über alle Channels.
  const channels: Float32Array[] = [];
  for (let c = 0; c < audioBuf.numberOfChannels; c += 1) {
    channels.push(audioBuf.getChannelData(c));
  }
  const totalSamples = audioBuf.length;
  const samplesPerBucket = Math.max(1, Math.floor(totalSamples / PEAK_BUCKETS));
  const peaks = new Float32Array(PEAK_BUCKETS);
  for (let b = 0; b < PEAK_BUCKETS; b += 1) {
    const start = b * samplesPerBucket;
    const end = Math.min(totalSamples, start + samplesPerBucket);
    let peak = 0;
    for (let i = start; i < end; i += 1) {
      let sample = 0;
      for (let c = 0; c < channels.length; c += 1) {
        const abs = Math.abs(channels[c][i]);
        if (abs > sample) sample = abs;
      }
      if (sample > peak) peak = sample;
    }
    peaks[b] = peak > 1 ? 1 : peak;
  }
  return { peaks, durationSec: audioBuf.duration };
}

/** Synchronously read a cached entry — useful for first-paint without
 *  triggering a network fetch. Returns null if nothing's been decoded yet. */
export function readCachedPeaks(src: string): WaveformPeaks | null {
  return cache.get(src) ?? null;
}
